import { Link } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Phone,
  Home,
  Calendar,
  UserCircle,
  Settings,
  Mic,
  Key,
  Cloud,
  PlusCircle,
  UserPlus,
  CalendarPlus,
  BarChart3,
  Shield,
  Globe,
  Zap,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface FeatureCard {
  title: string;
  description: string;
  icon: React.ReactNode;
  href: string;
  badge?: string;
  color: string;
}

interface QuickAction {
  title: string;
  description: string;
  icon: React.ReactNode;
  href: string;
  color: string;
}

export default function Overview() {
  const mainPages: FeatureCard[] = [
    {
      title: 'Stjórnborð',
      description: 'Yfirlit yfir öll kerfi, tölfræði og nýlega virkni',
      icon: <LayoutDashboard className="w-6 h-6" />,
      href: '/',
      color: 'from-blue-500 to-cyan-500',
    },
    {
      title: 'Ábendingar',
      description: 'Stjórna sölutækifærum og fylgja eftir fyrirspurnum',
      icon: <Users className="w-6 h-6" />,
      href: '/leads',
      badge: 'Virkur',
      color: 'from-violet-500 to-purple-500',
    },
    {
      title: 'Símtöl',
      description: 'Saga símtala, upptökur og afrit samtala',
      icon: <Phone className="w-6 h-6" />,
      href: '/calls',
      color: 'from-emerald-500 to-teal-500',
    },
    {
      title: 'Fasteignir',
      description: 'Gagnagrunnur eigna með myndum og upplýsingum',
      icon: <Home className="w-6 h-6" />,
      href: '/properties',
      color: 'from-amber-500 to-orange-500',
    },
    {
      title: 'Skoðanir',
      description: 'Dagatal skoðana og skipulagning tíma',
      icon: <Calendar className="w-6 h-6" />,
      href: '/viewings',
      color: 'from-pink-500 to-rose-500',
    },
    {
      title: 'Ráðgjafar',
      description: 'Stjórna teymi fasteignasala og verkefnum',
      icon: <UserCircle className="w-6 h-6" />,
      href: '/agents',
      color: 'from-indigo-500 to-blue-500',
    },
    {
      title: 'Stillingar',
      description: 'Kerfisstillingar, samþættingar og notendastjórnun',
      icon: <Settings className="w-6 h-6" />,
      href: '/settings',
      color: 'from-slate-500 to-gray-500',
    },
  ];

  const features: FeatureCard[] = [
    {
      title: 'Raddgervigreind',
      description: 'AI símsvörun sem svarar símtölum allan sólarhringinn',
      icon: <Mic className="w-5 h-5" />,
      href: '/settings',
      badge: 'AI',
      color: 'from-cyan-400 to-blue-500',
    },
    {
      title: 'Google Dagatal',
      description: 'Tengist Google Calendar fyrir sjálfvirka skoðunarbókun',
      icon: <Calendar className="w-5 h-5" />,
      href: '/settings',
      badge: 'Beta',
      color: 'from-green-400 to-emerald-500',
    },
    {
      title: 'Google OAuth',
      description: 'Örugg innskráning með Google reikningi',
      icon: <Shield className="w-5 h-5" />,
      href: '/login',
      color: 'from-red-400 to-rose-500',
    },
    {
      title: 'API Lyklar',
      description: 'Stjórna API aðgangi og samþættingum',
      icon: <Key className="w-5 h-5" />,
      href: '/settings',
      color: 'from-amber-400 to-yellow-500',
    },
    {
      title: 'Cloudflare',
      description: 'Hýst á Cloudflare Workers og D1 gagnagrunni',
      icon: <Cloud className="w-5 h-5" />,
      href: '#',
      badge: 'Innviðir',
      color: 'from-orange-400 to-red-500',
    },
    {
      title: 'Analytics',
      description: 'Tölfræði og skýrslur um símtöl og ábendingar',
      icon: <BarChart3 className="w-5 h-5" />,
      href: '/',
      color: 'from-purple-400 to-violet-500',
    },
  ];

  const quickActions: QuickAction[] = [
    {
      title: 'Búa til ábendings',
      description: 'Skrá nýja ábendings handvirkt',
      icon: <PlusCircle className="w-5 h-5" />,
      href: '/leads',
      color: 'from-violet-500 to-purple-500',
    },
    {
      title: 'Bóka skoðun',
      description: 'Búa til nýja skoðunartíma',
      icon: <CalendarPlus className="w-5 h-5" />,
      href: '/viewings',
      color: 'from-pink-500 to-rose-500',
    },
    {
      title: 'Bæta við ráðgjafa',
      description: 'Skrá nýjan ráðgjafa í kerfið',
      icon: <UserPlus className="w-5 h-5" />,
      href: '/agents',
      color: 'from-indigo-500 to-blue-500',
    },
    {
      title: 'Ný eign',
      description: 'Bæta við fasteign í gagnagrunn',
      icon: <Home className="w-5 h-5" />,
      href: '/properties',
      color: 'from-amber-500 to-orange-500',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/5 via-transparent to-purple-600/5" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-blue-400/10 to-purple-600/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-cyan-400/10 to-blue-600/10 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center space-y-4">
            <h1 className="text-5xl sm:text-6xl font-bold bg-gradient-to-br from-gray-900 via-blue-900 to-gray-800 bg-clip-text text-transparent tracking-tight">
              Prima Yfirlit
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto font-light">
              Heildaryfirlit yfir alla eiginleika, síður og aðgerðir í Prima kerfinu
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 space-y-12">
        {/* Main Pages */}
        <section className="space-y-6">
          <div className="flex items-center gap-3">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gray-300 to-transparent" />
            <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Aðalsíður</h2>
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gray-300 to-transparent" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {mainPages.map((page, index) => (
              <Link
                key={page.href}
                to={page.href}
                className="group relative overflow-hidden rounded-2xl bg-white border border-gray-200/50 p-6 shadow-sm hover:shadow-xl transition-all duration-500 hover:scale-[1.02]"
                style={{
                  animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`,
                }}
              >
                <div className={cn("absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-5 transition-opacity duration-500", page.color)} />

                <div className="relative space-y-4">
                  <div className="flex items-start justify-between">
                    <div className={cn(
                      "w-12 h-12 rounded-xl bg-gradient-to-br flex items-center justify-center text-white",
                      "shadow-lg transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-500",
                      page.color
                    )}>
                      {page.icon}
                    </div>
                    {page.badge && (
                      <span className="px-3 py-1 text-xs font-semibold rounded-full bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-700 border border-blue-200">
                        {page.badge}
                      </span>
                    )}
                  </div>

                  <div className="space-y-2">
                    <h3 className="text-xl font-bold text-gray-900 group-hover:text-blue-700 transition-colors">
                      {page.title}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {page.description}
                    </p>
                  </div>

                  <div className="flex items-center text-blue-600 text-sm font-medium pt-2 group-hover:translate-x-2 transition-transform">
                    <span>Opna</span>
                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Features */}
        <section className="space-y-6">
          <div className="flex items-center gap-3">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gray-300 to-transparent" />
            <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Eiginleikar</h2>
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gray-300 to-transparent" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {features.map((feature, index) => (
              <Link
                key={feature.href + feature.title}
                to={feature.href}
                className="group relative overflow-hidden rounded-xl bg-white/80 backdrop-blur-sm border border-gray-200/50 p-5 hover:bg-white hover:shadow-lg transition-all duration-300"
                style={{
                  animation: `fadeInUp 0.5s ease-out ${index * 0.08}s both`,
                }}
              >
                <div className="flex items-start gap-4">
                  <div className={cn(
                    "w-10 h-10 rounded-lg bg-gradient-to-br flex items-center justify-center text-white flex-shrink-0",
                    "group-hover:scale-110 transition-transform duration-300",
                    feature.color
                  )}>
                    {feature.icon}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h3 className="text-base font-semibold text-gray-900 group-hover:text-blue-700 transition-colors">
                        {feature.title}
                      </h3>
                      {feature.badge && (
                        <span className="px-2 py-0.5 text-xs font-medium rounded bg-gray-100 text-gray-700 flex-shrink-0">
                          {feature.badge}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 leading-snug">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Quick Actions */}
        <section className="space-y-6">
          <div className="flex items-center gap-3">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gray-300 to-transparent" />
            <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Hraðaðgerðir</h2>
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gray-300 to-transparent" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {quickActions.map((action, index) => (
              <Link
                key={action.href + action.title}
                to={action.href}
                className="group relative overflow-hidden rounded-xl bg-gradient-to-br p-[2px] hover:shadow-xl transition-all duration-300 hover:scale-105"
                style={{
                  animation: `fadeInUp 0.4s ease-out ${index * 0.1}s both`,
                }}
              >
                <div className={cn("absolute inset-0 bg-gradient-to-br opacity-100 group-hover:opacity-100", action.color)} />
                <div className="relative bg-white rounded-[10px] p-5 h-full">
                  <div className="space-y-3">
                    <div className={cn(
                      "w-10 h-10 rounded-lg bg-gradient-to-br flex items-center justify-center text-white",
                      "group-hover:scale-110 group-hover:rotate-6 transition-all duration-300",
                      action.color
                    )}>
                      {action.icon}
                    </div>

                    <div>
                      <h3 className="text-base font-bold text-gray-900 mb-1">
                        {action.title}
                      </h3>
                      <p className="text-xs text-gray-600 leading-relaxed">
                        {action.description}
                      </p>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* System Info */}
        <section className="mt-12">
          <div className="rounded-2xl bg-gradient-to-br from-slate-900 to-blue-900 p-8 text-white shadow-2xl overflow-hidden relative">
            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/20 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-500/20 rounded-full blur-3xl" />

            <div className="relative grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="space-y-2">
                <Globe className="w-8 h-8 mb-3 text-blue-300" />
                <h3 className="text-lg font-bold">Kerfi</h3>
                <p className="text-sm text-blue-100">
                  Cloudflare Workers + D1
                </p>
                <p className="text-xs text-blue-200/70">
                  Hraðvirkt og skalanlegt
                </p>
              </div>

              <div className="space-y-2">
                <Zap className="w-8 h-8 mb-3 text-purple-300" />
                <h3 className="text-lg font-bold">AI Vélar</h3>
                <p className="text-sm text-purple-100">
                  Google Dialogflow CX + Gemini
                </p>
                <p className="text-xs text-purple-200/70">
                  Náttúruleg raddsamskipti
                </p>
              </div>

              <div className="space-y-2">
                <Shield className="w-8 h-8 mb-3 text-green-300" />
                <h3 className="text-lg font-bold">Öryggi</h3>
                <p className="text-sm text-green-100">
                  Google OAuth + JWT
                </p>
                <p className="text-xs text-green-200/70">
                  Örugg auðkenning
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}
